import React, { useEffect, useRef, useState } from 'react';

import { translateComponent } from '@/utils/tolgee/translateComponent';
import {
  BarChartData3,
  LomavisBarChart3
} from '@/lomavisSharedUi/atoms/charts/LomavisBarChart3';

export type MetricName =
  | 'impressions'
  | 'reach'
  | 'reactions'
  | 'shares'
  | 'comments'
  | 'engagement';

interface ILomavisPlatformContentType1Props {
  /** Data keyed by platform and post type, metrics restricted to known names */
  data: Record<
    string, // platform
    Record<
      string, // postType
      Partial<Record<MetricName, number>>
    >
  >;
  zoomControl?: boolean;
}

/**
 * Molecules: ILomavisPlatformContentType1
 */
export function getAllMetricKeys(data): string[] {
  const keys = new Set<string>();

  for (const platformObj of Object.values(data)) {
    for (const postTypeObj of Object.values(platformObj)) {
      Object.keys(postTypeObj).forEach((metric) => keys.add(metric));
    }
  }

  return Array.from(keys);
}
export function getAllMetricTextKeys(
  data: Record<string, any>,
  t: (key: string) => string
): Array<{ key: string; text: string }> {
  const keys = new Map<string, string>();

  for (const platformObj of Object.values(data)) {
    for (const postTypeObj of Object.values(platformObj)) {
      Object.keys(postTypeObj).forEach((metric) => {
        if (!keys.has(metric)) {
          keys.set(metric, t(metric));
        }
      });
    }
  }

  return Array.from(keys.entries()).map(([key, text]) => ({ key, text }));
}

function getSeriesByMetric(
  analytics: Record<
    string, // platform (e.g., "facebook", "instagram")
    Record<
      string, // postType (e.g., "story", "reel")
      Partial<Record<MetricName, number>>
    >
  >,
  metric: string
): BarChartData3[] {
  return Object.entries(analytics).map(([platform, posts]) => {
    const data: Record<string, number> = {};
    for (const [postType, metrics] of Object.entries(posts)) {
      data[postType] = metrics?.[metric] ?? 0;
    }
    return { label: platform, data };
  });
}

export const ILomavisPlatformContentType1: React.FC<
  ILomavisPlatformContentType1Props
> = ({ data, zoomControl }) => {
  const t = translateComponent('ILomavisPlatformContentType1');

  const dropdownRef = useRef(null);

  const sortOptionsTextKeys = getAllMetricTextKeys(data, t);

  // Default to an empty object with empty strings if no options are available
  const defaultOption = { key: '', text: '' };
  const [sortOption, setSortOption] = useState(sortOptionsTextKeys[0] || defaultOption);
  const [showDropdown, setShowDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        showDropdown &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  return (
    <div className="w-100 h-600px">
      <div className="w-100 d-flex justify-content-end">
        <div className="dropdown text-end" ref={dropdownRef}>
          <button
            className="btn btn-secondary btn-sm dropdown-toggle text-capitalize"
            type="button"
            aria-expanded={showDropdown}
            onClick={() => setShowDropdown((prev) => !prev)}
          >
            {sortOption.text}
          </button>
          <ul className={`dropdown-menu${showDropdown ? ' show' : ''}`}>
            {sortOptionsTextKeys.map((item) => (
              <li key={item.key} className="w-100 p-1">
                <button
                  className={`text-capitalize dropdown-item${
                    item.key === sortOption.key ? ' active' : ''
                  }`}
                  type="button"
                  onClick={() => {
                    setSortOption(item);
                    setShowDropdown(false);
                  }}
                >
                  {item.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="h-100">
        {sortOptionsTextKeys.length > 0 ? (
          <LomavisBarChart3
            data={getSeriesByMetric(data, sortOption.key)}
            zoomControl={zoomControl}
          />
        ) : (
          <div className="d-flex justify-content-center align-items-center h-100">
            <p>{t('no_data_available')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
